/**
 * Per-user permissions are split across two distinct sets:
 * - `SiteRole`, which is set per-user and governs 'superuser' permissions
 *   applied to the entire AdHub site.  This property is set against the
 *   user's database entry. Most users will have a value of 'default' here,
 *   which means no special permissions, and the evaluated role is
 *   instead...
 * - `DocumentRole`, which is a user+document binding, where a 'document' is
 *   either a Client, Project or Creative. Each binding is stored against a
 *   document, on the field 'members', and maps user IDs to a DocumentRole
 *   value.
 */
import { arrayMapFilter } from 'utils/Array';

function isEnumStringValue(
    enumObj: Record<string, string>,
    x: unknown
): boolean {
    return (
        typeof x === 'string' &&
        (Object.values(enumObj) as ReadonlyArray<string>).includes(x)
    );
}

export enum SiteRole {
    /**
     * User has no special status - permissions are granted per-document.
     */
    DEFAULT = 'default',

    /**
     * User has read/write access to all documents, but not to other users.
     * Can create new client documents.
     *
     * Note: Document creation requires edit permission (via a DocumentRole
     * binding) on the parent document. Because 'clients' is a root-level
     * collection, there is no parent collection, hence the need to specify
     * it through some other means.
     */
    MANAGER = 'manager',

    /**
     * Superset of MANAGER. User has read/write access to entire database,
     * including the ability to add, remove, or modify users.
     */
    ADMIN = 'admin',
}

export const SITE_ROLE_LABELS: Readonly<Record<SiteRole, string>> = {
    [SiteRole.DEFAULT]: 'Default',
    [SiteRole.MANAGER]: 'Manager',
    [SiteRole.ADMIN]: 'Admin',
};

export const SITE_ROLE_ORDER: ReadonlyArray<SiteRole> = [
    SiteRole.ADMIN,
    SiteRole.MANAGER,
    SiteRole.DEFAULT,
];

export function isSuperUserSiteRole(
    x: unknown
): x is SiteRole.ADMIN | SiteRole.MANAGER {
    return x === SiteRole.ADMIN || x === SiteRole.MANAGER;
}

export function isSiteRole(x: unknown): x is SiteRole {
    return isEnumStringValue(SiteRole, x);
}

export enum DocumentRole {
    /**
     * User can view the document that this binding is applied to.
     */
    VIEW = 'view',

    /**
     * User can edit the document that this binding is applied to,
     * including the ability to manage other DocumentRole bindings.
     */
    EDIT = 'edit',
}

export const DOCUMENT_ROLE_LABELS: Readonly<Record<DocumentRole, string>> = {
    [DocumentRole.VIEW]: 'Viewer',
    [DocumentRole.EDIT]: 'Editor',
};

export const DOCUMENT_ROLE_ORDER: ReadonlyArray<DocumentRole> = [
    DocumentRole.EDIT,
    DocumentRole.VIEW,
];

export const DOCUMENT_ROLE_LABEL_NONE = 'No Access';

export function isDocumentRole(x: unknown): x is DocumentRole {
    return isEnumStringValue(DocumentRole, x);
}

export type DocumentRoleBinding = {
    role: DocumentRole;
    uid: string;
};

// Persistence format - bindings are stored as a map of uid: { uid, role }
export interface DocumentRoleBindingLookup {
    [uid: string]: DocumentRoleBinding;
}

export function unpackDocumentRoleBindingLookup(
    drbLookup: unknown
): DocumentRoleBinding[] {
    const logPrefix = `[unpackDocumentRoleBindingLookup]`;
    if (!drbLookup) {
        console.warn(`${logPrefix} Expected object, got ${drbLookup}`);
        return [];
    }

    if (typeof drbLookup !== 'object') {
        console.warn(`${logPrefix} Expected object, got ${typeof drbLookup}`);
        return [];
    }

    return arrayMapFilter(
        Object.entries(drbLookup),
        ([uidKey, binding]): DocumentRoleBinding | undefined => {
            // Having the uid as both the key and inside the object is
            // indeed redundant - and the Firestore rules evaluate against
            // the key and the .role - the .uid property is never actually
            // consulted. So we conform what we read instead of considering
            // it a failed validation, because that reflects how the rules
            // are evaluated.
            //
            // Hence only the role is validated, deliberately, rather than
            // the whole binding: a stored entry missing .uid is still one
            // the rules would honor, and rejecting it here would report a
            // member as having no access while they can still edit. A
            // reader of stored permissions must never under-report access.
            //
            // This mirrors userHasDocumentRole in config/firestore.rules:
            // the uid is a key of `members`, and members[uid].role is a
            // known role.
            const entry =
                typeof binding === 'object' && binding !== null
                    ? (binding as { role?: unknown; uid?: unknown })
                    : undefined;
            const role = entry?.role;
            if (!isDocumentRole(role)) {
                const jKey = JSON.stringify(uidKey);
                const jBinding = JSON.stringify(binding);
                console.warn(
                    `${logPrefix} Dropping binding with unusable role ${jKey}: ${jBinding}`
                );

                // Note the opposite reasoning applies here: an unreadable
                // role grants nothing under the rules, so dropping it
                // agrees with what will actually be enforced.
                return undefined;
            }

            const uid = entry?.uid;
            if (uidKey !== uid) {
                // All that said, at least make a little noise about it
                console.warn(
                    `${logPrefix} Correcting uid mismatch:` +
                        ` key (canonical)="${uidKey}"` +
                        ` binding.uid="${uid}"`
                );
            }
            return { uid: uidKey, role };
        }
    );
}

export function packDocumentRoleBindingLookup(
    bindings: ReadonlyArray<DocumentRoleBinding>
): DocumentRoleBindingLookup {
    return Object.fromEntries(
        bindings.map((binding) => [binding.uid, binding] as const)
    );
}

export function cloneDocumentRoleBindingLookup(
    lookup: DocumentRoleBindingLookup
): DocumentRoleBindingLookup {
    return packDocumentRoleBindingLookup(
        unpackDocumentRoleBindingLookup(lookup)
    );
}

export interface MemberAccess {
    memberKeys: string[];
    members: DocumentRoleBindingLookup;
}

export function copyMemberAccess(
    source: MemberAccess,
    destination: MemberAccess
): void {
    destination.members = cloneDocumentRoleBindingLookup(source.members);
    // Derived rather than copied, so it can't disagree with `members` if
    // the clone dropped an unusable binding; matches the backend's
    // patchMembers in functions/src/permissions/documentbind.ts.
    destination.memberKeys = Object.keys(destination.members);
}
